/**
 * CLI Tools Page
 *
 * Catalog of LUKHAS command-line utilities and development tools
 */

import React, { useState } from 'react'
import { Terminal, Package, Code2, Search, Filter, ExternalLink, Copy, CheckCircle } from 'lucide-react'

interface CLITool {
  name: string
  category: string
  description: string
  command: string
  path: string
  tags: string[]
}

// Comprehensive CLI tools catalog
const CLI_TOOLS: CLITool[] = [
  // Tone & Quality
  {
    name: 'Tone Validator',
    category: 'Quality',
    description: 'Validate commit messages and documentation against LUKHAS tone standards',
    command: 'python tools/tone/lukhas_tone_validator.py',
    path: 'tools/tone/lukhas_tone_validator.py',
    tags: ['validation', 'quality', 'tone', 'commits'],
  },

  // Journal & Development
  {
    name: 'Solo Dev Support',
    category: 'Development',
    description: 'AI-assisted solo developer workflow support and context management',
    command: 'python tools/journal/solo_dev_support.py',
    path: 'tools/journal/solo_dev_support.py',
    tags: ['workflow', 'ai', 'context', 'development'],
  },
  {
    name: 'Insight Analyzer',
    category: 'Analysis',
    description: 'Analyze development journals for insights and patterns',
    command: 'python tools/journal/insight_analyzer.py',
    path: 'tools/journal/insight_analyzer.py',
    tags: ['analysis', 'insights', 'journal', 'patterns'],
  },
  {
    name: 'Learning Assistant',
    category: 'Development',
    description: 'AI-powered learning assistant for LUKHAS development',
    command: 'python tools/journal/learning_assistant.py',
    path: 'tools/journal/learning_assistant.py',
    tags: ['learning', 'ai', 'assistant', 'education'],
  },

  // Memory & Consolidation
  {
    name: 'Consolidate Memory',
    category: 'Memory',
    description: 'Consolidate and optimize LUKHAS memory systems',
    command: 'python tools/consolidate_memory.py',
    path: 'tools/consolidate_memory.py',
    tags: ['memory', 'optimization', 'consolidation'],
  },
  {
    name: '2030 Full Consolidator',
    category: 'Memory',
    description: 'Full system consolidation for long-term memory optimization',
    command: 'python tools/2030_full_consolidator.py',
    path: 'tools/2030_full_consolidator.py',
    tags: ['consolidation', 'memory', 'optimization', 'long-term'],
  },

  // Code Quality
  {
    name: 'Fix Misplaced Imports',
    category: 'Refactoring',
    description: 'Automatically fix misplaced imports across codebase',
    command: 'python tools/fix_misplaced_imports.py',
    path: 'tools/fix_misplaced_imports.py',
    tags: ['imports', 'refactoring', 'automation', 'fix'],
  },
  {
    name: 'Fix Streamlit Imports',
    category: 'Refactoring',
    description: 'Fix Streamlit-specific import issues',
    command: 'python tools/fix_streamlit_imports.py',
    path: 'tools/fix_streamlit_imports.py',
    tags: ['streamlit', 'imports', 'fix'],
  },
  {
    name: 'Fix Trinity to Constellation',
    category: 'Refactoring',
    description: 'Migrate Trinity Framework references to Constellation Framework',
    command: 'python tools/fix_trinity_to_constellation.py',
    path: 'tools/fix_trinity_to_constellation.py',
    tags: ['migration', 'constellation', 'refactoring'],
  },

  // Attestation & Compliance
  {
    name: 'Generate DPA',
    category: 'Compliance',
    description: 'Generate Data Processing Agreement documents',
    command: 'python tools/generate_dpa.py',
    path: 'tools/generate_dpa.py',
    tags: ['compliance', 'dpa', 'legal', 'gdpr'],
  },
  {
    name: 'Collect Attestation',
    category: 'Security',
    description: 'Collect security attestations and compliance data',
    command: 'python tools/collect_attestation.py',
    path: 'tools/collect_attestation.py',
    tags: ['security', 'attestation', 'compliance'],
  },

  // MATRIZ Tools
  {
    name: 'MATRIZ Audit Discovery',
    category: 'Audit',
    description: 'Discover and audit MATRIZ cognitive nodes',
    command: 'python tools/matriz_audit_discovery.py',
    path: 'tools/matriz_audit_discovery.py',
    tags: ['matriz', 'audit', 'discovery', 'cognitive'],
  },
  {
    name: 'Pre-commit MATRIZ Check',
    category: 'Quality',
    description: 'Pre-commit hook for MATRIZ compliance validation',
    command: 'python tools/pre_commit_matrix_check.py',
    path: 'tools/pre_commit_matrix_check.py',
    tags: ['pre-commit', 'matriz', 'validation', 'quality'],
  },

  // Promotion & Deployment
  {
    name: 'Promotion Selector',
    category: 'Deployment',
    description: 'Select components for promotion from candidate to production lane',
    command: 'python tools/promotion_selector.py',
    path: 'tools/promotion_selector.py',
    tags: ['promotion', 'deployment', 'lanes'],
  },
  {
    name: 'Check Policy',
    category: 'Security',
    description: 'Validate security policies and ABAS constraints',
    command: 'python tools/check_policy.py',
    path: 'tools/check_policy.py',
    tags: ['security', 'policy', 'abas', 'validation'],
  },

  // Analysis
  {
    name: 'Keyword Extractor',
    category: 'Analysis',
    description: 'Extract keywords and concepts from documentation',
    command: 'python tools/keyword_extractor.py',
    path: 'tools/keyword_extractor.py',
    tags: ['analysis', 'keywords', 'nlp', 'documentation'],
  },
]

export const CLIToolsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null)

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(CLI_TOOLS.map(tool => tool.category)))]

  // Filter tools
  const filteredTools = CLI_TOOLS.filter(tool => {
    const matchesSearch =
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))

    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory

    return matchesSearch && matchesCategory
  })

  // Copy command to clipboard
  const handleCopyCommand = (command: string) => {
    navigator.clipboard.writeText(command)
    setCopiedCommand(command)
    setTimeout(() => setCopiedCommand(null), 2000)
  }

  // Get category color
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Quality': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Development': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Analysis': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'Memory': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'Refactoring': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Compliance': 'bg-red-500/20 text-red-400 border-red-500/30',
      'Security': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Audit': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      'Deployment': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    }
    return colors[category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg">
                <Terminal className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  CLI Tools
                </h1>
                <p className="text-gray-400 mt-1">
                  Λ Development & Automation Utilities
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{filteredTools.length}</p>
                <p className="text-xs text-gray-400">Tools Available</p>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tools, descriptions, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredTools.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No tools found matching your criteria</p>
            <p className="text-gray-500 text-sm mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTools.map((tool, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg p-6 hover:border-blue-500/50 transition-all group"
              >
                {/* Tool Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Code2 className="w-5 h-5 text-blue-400" />
                      <h3 className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                        {tool.name}
                      </h3>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {tool.description}
                    </p>
                  </div>

                  <span className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ml-3 ${getCategoryColor(tool.category)}`}>
                    {tool.category}
                  </span>
                </div>

                {/* Command */}
                <div className="relative mb-4">
                  <div className="bg-gray-950/50 border border-gray-800 rounded-lg p-3 font-mono text-sm text-gray-300 pr-10">
                    {tool.command}
                  </div>
                  <button
                    onClick={() => handleCopyCommand(tool.command)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 hover:bg-gray-800 rounded transition-colors"
                    title="Copy command"
                  >
                    {copiedCommand === tool.command ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Tags & Path */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {tool.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-2 py-1 bg-gray-800/50 text-gray-400 text-xs rounded"
                      >
                        #{tag}
                      </span>
                    ))}
                    {tool.tags.length > 3 && (
                      <span className="px-2 py-1 bg-gray-800/50 text-gray-500 text-xs rounded">
                        +{tool.tags.length - 3} more
                      </span>
                    )}
                  </div>

                  <a
                    href={`https://github.com/LukhasAI/Lukhas/blob/main/${tool.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-xs text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    <span>View Source</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.filter(c => c !== 'all').map(category => {
            const count = CLI_TOOLS.filter(t => t.category === category).length
            return (
              <div
                key={category}
                className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg text-center"
              >
                <p className="text-2xl font-bold text-white">{count}</p>
                <p className="text-xs text-gray-400 mt-1">{category}</p>
              </div>
            )
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-400 mb-2">💡 Using CLI Tools</h3>
          <div className="text-gray-300 space-y-2 text-sm">
            <p>
              All CLI tools are located in the <code className="px-2 py-0.5 bg-gray-800 rounded">tools/</code> directory and can be executed from the LUKHAS root.
            </p>
            <p>
              <strong>Note:</strong> This catalog shows {CLI_TOOLS.length} featured tools. The full repository contains 589+ utilities across all categories.
            </p>
            <p className="text-gray-400 mt-3">
              For documentation on specific tools, use: <code className="px-2 py-0.5 bg-gray-800 rounded">python tools/[tool-name].py --help</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CLIToolsPage
